import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import { useHistory } from 'react-router'
import styled from 'styled-components'

import { FlexBox } from '../../../components/Base/Base'
import { Text } from '../../../components/Text/Text'
import { formatDate } from '../../../helpers/formatters'
import { useSafetyScore } from '../../../hooks/query/useSafetyScore'
import { useSafetyScoreRanges } from '../../../hooks/query/useSafetyScoreRanges'
import { User } from '../../../models/user'
import { blueGray, lightGray1, primaryBlack, white } from '../../../theme/colors'
import { OverviewSidebarScore } from './OverviewSidebarScore'
import { SafetyInfoTooltip } from './SafetyInfoTooltip'
import { SafetyScoreHeaderItem } from './SafetyScoreHeaderItem'
import { ScorePill } from './ScorePill'

interface OverviewSectionProps {
  user?: User
  isHeader?: boolean
  isInsideSidebar?: boolean
}

export function OverviewSafetyScore({ user, isHeader, isInsideSidebar }: OverviewSectionProps) {
  const history = useHistory()
  const { safetyScore } = useSafetyScore(user?.id ?? '', { enabled: !!user?.id })
  const { safetyScoreRanges } = useSafetyScoreRanges()

  const openSafetyScoreLink = () => {
    return history.push(`/compliance-safety-tool/${user?.id}#safety-score`)
  }

  if (isInsideSidebar) {
    return (
      <OverviewSidebarScore
        title="SAFETY SCORE"
        score={{
          score: safetyScore?.safetyScore,
          scoreColor: safetyScore?.safetyScoreColor,
          scoreBackgroundColor: safetyScore?.safetyScoreBackgroundColor,
          scoreLabel: safetyScore?.safetyScoreLabel,
        }}
        tooltip={
          <SafetyInfoTooltip
            title="Safety scores are based on a driver’s lifecycle and may improve as a user continues to drive."
            ranges={safetyScoreRanges}
          />
        }
        onClick={openSafetyScoreLink}
      />
    )
  }

  return (
    <FlexBox
      isColumn
      gap="20px"
      mb={3}
      onClick={isHeader ? undefined : openSafetyScoreLink}
      styles={`
        background-color: ${!isHeader ? white : null};
        padding: ${isHeader || isInsideSidebar ? '0' : '24px 32px'};
        border-radius: 2px;
      `}
    >
      <FlexBox justifyContent="space-between" alignItems="center">
        <FlexBox justifyContent="flex-start" gap="8px">
          <Text variant="uppercaseLabel3" mt={0.5}>
            SAFETY SCORE
          </Text>
          <SafetyInfoTooltip
            title="Safety scores are based on a driver’s lifecycle and may improve as a user continues to drive."
            ranges={safetyScoreRanges}
          />
        </FlexBox>
        {!isHeader && <KeyboardArrowRightIcon htmlColor={blueGray} />}
      </FlexBox>

      <FlexBox gap="32px">
        <FlexBox isColumn gap="16px">
          <SafetyScoreContainer
            gap="16px"
            justifyContent="flex-start"
            alignItems="center"
            $isInsideSidebar={isInsideSidebar}
          >
            <ScorePill
              score={safetyScore?.safetyScore}
              color={safetyScore?.safetyScoreColor}
              backgroundColor={safetyScore?.safetyScoreBackgroundColor}
            />
            <Text
              variant="header2"
              fontColor={safetyScore?.safetyScoreColor ?? primaryBlack}
              mt={0.5}
              styles={isInsideSidebar ? 'font-size: 16px;' : 'font-size: 20px;'}
            >
              {safetyScore?.safetyScoreLabel}
            </Text>
          </SafetyScoreContainer>

          <Text variant="label3" fontColor={lightGray1}>
            Updated on {safetyScore?.runTs && formatDate(safetyScore?.runTs)}
          </Text>
        </FlexBox>

        <FlexBox isColumn gap="8px" flex={isInsideSidebar ? 1 : undefined}>
          <SafetyScoreHeaderItem
            title="Driving behaviors"
            color={safetyScore?.drivingBehaviorColor}
            label={safetyScore?.drivingBehaviorLabel}
            score={safetyScore?.drivingBehaviorScore}
          />

          <SafetyScoreHeaderItem
            title="Accidents & violations"
            color={safetyScore?.accidentsViolationsColor}
            label={safetyScore?.accidentsViolationsLabel}
            score={safetyScore?.accidentsViolationsScore}
          />

          <SafetyScoreHeaderItem
            title="Compliance"
            color={safetyScore?.onRoadComplianceColor}
            label={safetyScore?.onRoadComplianceLabel}
            score={safetyScore?.onRoadComplianceScore}
          />
        </FlexBox>
      </FlexBox>
    </FlexBox>
  )
}

export const SafetyScoreContainer = styled(FlexBox)<{ $isInsideSidebar?: boolean }>`
  width: ${({ $isInsideSidebar }) => ($isInsideSidebar ? 100 : 256)}px;
`
